import { useRef, useState } from "react";
import { uploadEmployeeFile } from "@/lib/fileUpload";
import type { Employee } from "@/types/employee";
import { useUser } from "@/context/UserContext";

interface IdentityFormProps {
  employee: Employee | null;
  onUpdate: (updated: Employee) => void;
  onToast: (msg: string, type?: "success" | "error") => void;
}

type UploadField = "id_card_front_url" | "id_card_back_url";

const fieldLabels: Record<UploadField, string> = {
  id_card_front_url: "身分證正面",
  id_card_back_url: "身分證反面",
};

export default function IdentityForm({ employee, onUpdate, onToast }: IdentityFormProps) {
  const [uploading, setUploading] = useState<UploadField | null>(null);
  const frontRef = useRef<HTMLInputElement>(null);
  const backRef = useRef<HTMLInputElement>(null);
  const { refreshProfileComplete } = useUser();

  const fileInputRefs: Record<UploadField, React.RefObject<HTMLInputElement | null>> = {
    id_card_front_url: frontRef,
    id_card_back_url: backRef,
  };

  const handleUpload = async (field: UploadField, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !employee?.id) {
      if (!employee?.id) onToast("無法上傳：未找到員工資料", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      onToast("圖片過大，請選擇 5MB 以下的檔案", "error");
      return;
    }

    setUploading(field);
    try {
      const publicUrl = await uploadEmployeeFile(file, employee.id, field);
      onUpdate({ ...employee, [field]: publicUrl });
      onToast(`${fieldLabels[field]}已上傳`, "success");
      refreshProfileComplete();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "上傳失敗";
      onToast(`${fieldLabels[field]}上傳錯誤：${msg}`, "error");
    }
    setUploading(null);
  };

  const renderUploadRow = (field: UploadField) => {
    const url = employee?.[field];
    const label = fieldLabels[field];
    const isUploading = uploading === field;

    return (
      <div key={field} className="flex items-center justify-between py-3 border-b border-background-100 last:border-b-0">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-foreground-700">
            {label} <span className="text-accent-500">*</span>
          </p>
          <p className="text-xs text-foreground-400 mt-0.5 truncate">
            {isUploading ? "處理中..." : url ? "已上傳" : "尚未上傳"}
          </p>
        </div>
        <input
          ref={fileInputRefs[field]}
          type="file"
          accept="image/*,.pdf"
          className="hidden"
          onChange={(e) => handleUpload(field, e)}
        />
        <button
          onClick={() => fileInputRefs[field].current?.click()}
          disabled={isUploading}
          className="ml-3 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-foreground-600 border border-background-300 rounded-lg hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50 shrink-0 whitespace-nowrap"
        >
          {isUploading ? (
            <i className="ri-loader-4-line animate-spin" />
          ) : (
            <i className="ri-upload-cloud-2-line" />
          )}
          上傳
        </button>
      </div>
    );
  };

  return (
    <div className="bg-background-50 border border-background-200 rounded-xl p-5 md:p-6">
      <h2 className="text-base font-bold text-foreground-900 mb-1">身分驗證資料</h2>
      <p className="text-xs text-foreground-500 mb-3">身分證正反面皆為必填</p>
      <div className="divide-y divide-background-100">
        {renderUploadRow("id_card_front_url")}
        {renderUploadRow("id_card_back_url")}
      </div>
    </div>
  );
}